import baseApi from "./RootBaseApi";
import type {
  HarborFinanceFilters,
  HarborRevenueTimeSeriesItem,
  HarborRevenueByCategoryItem,
  HarborTopItemsResponse,
  HarborUsageResponse,
} from "@/types/harbor-finance-reports.types";

export const HarborFinanceReportsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHarborRevenueTimeSeries: builder.query<
      HarborRevenueTimeSeriesItem[],
      HarborFinanceFilters
    >({
      query: (params) => ({
        url: "admin/reports/harbors/revenue/time-series",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown): HarborRevenueTimeSeriesItem[] => {
        const raw = (response as { data?: unknown })?.data;
        return Array.isArray(raw) ? (raw as HarborRevenueTimeSeriesItem[]) : [];
      },
      providesTags: ["RevenueReports"],
    }),

    getHarborRevenueBreakdown: builder.query<
      HarborRevenueByCategoryItem[],
      HarborFinanceFilters
    >({
      query: (params) => ({
        url: "admin/reports/harbors/revenue/breakdown",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown): HarborRevenueByCategoryItem[] => {
        const raw = (response as { data?: unknown })?.data;
        return Array.isArray(raw) ? (raw as HarborRevenueByCategoryItem[]) : [];
      },
      providesTags: ["RevenueReports"],
    }),

    getHarborUsage: builder.query<HarborUsageResponse, HarborFinanceFilters>({
      query: (params) => ({
        url: "admin/reports/harbors/usage",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown): HarborUsageResponse => {
        const raw = (response as { data?: { items?: unknown[] } })?.data;
        const items = Array.isArray(raw?.items) ? raw?.items : [];
        return { items } as HarborUsageResponse;
      },
      providesTags: ["RevenueReports"],
    }),

    getHarborTopItems: builder.query<
      HarborTopItemsResponse,
      HarborFinanceFilters
    >({
      query: (params) => ({
        url: "admin/reports/harbors/top-items",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown): HarborTopItemsResponse => {
        const raw = (
          response as {
            data?: {
              services?: unknown[];
              berths?: unknown[];
              addons?: unknown[];
            };
          }
        )?.data;
        const services = Array.isArray(raw?.services) ? raw?.services : [];
        const berths = Array.isArray(raw?.berths) ? raw?.berths : [];
        const addons = Array.isArray(raw?.addons) ? raw?.addons : [];
        return { services, berths, addons } as HarborTopItemsResponse;
      },
      providesTags: ["RevenueReports"],
    }),

    exportHarborFinanceCsv: builder.query<Blob, HarborFinanceFilters>({
      query: (params) => ({
        url: "admin/reports/harbors/export/csv",
        method: "GET",
        params,
        responseType: "blob",
      }),
      transformResponse: (response: unknown): Blob => response as Blob,
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetHarborRevenueTimeSeriesQuery,
  useGetHarborRevenueBreakdownQuery,
  useGetHarborUsageQuery,
  useGetHarborTopItemsQuery,
  useLazyExportHarborFinanceCsvQuery,
} = HarborFinanceReportsService;
